import React, { useState, useEffect } from 'react';
import { Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

/**
 * Auto Refresh Control Component
 * Displays last updated time and auto-refresh toggle
 */
const AutoRefreshControl = ({
  lastUpdated,
  autoRefreshEnabled,
  refreshInterval,
  onToggleAutoRefresh,
  onRefreshIntervalChange,
  onManualRefresh,
  className = ''
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second to make the timer live
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const now = currentTime;
    const updated = new Date(timestamp);
    const diffMs = now - updated;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    
    if (diffSecs < 60) {
      return `${diffSecs} second${diffSecs !== 1 ? 's' : ''} ago`;
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else {
      const diffHours = Math.floor(diffMins / 60);
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    }
  };

  const getDataFreshnessColor = (timestamp) => {
    if (!timestamp) return 'text-muted-foreground';
    
    const now = new Date();
    const updated = new Date(timestamp);
    const diffMs = now - updated;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 5) return 'text-green-600';
    if (diffMins < 15) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {/* Last Updated */}
      <div className="flex items-center gap-2 text-sm">
        <Clock className={`w-4 h-4 ${getDataFreshnessColor(lastUpdated)}`} />
        <span className={getDataFreshnessColor(lastUpdated)}>
          Updated {formatLastUpdated(lastUpdated)}
        </span>
      </div>

      {/* Auto Refresh Toggle */}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={autoRefreshEnabled}
            onCheckedChange={onToggleAutoRefresh}
          />
          <span className="text-sm">Auto-refresh</span>
        </label>

        {autoRefreshEnabled && (
          <Select
            value={refreshInterval.toString()}
            onValueChange={(value) => onRefreshIntervalChange(Number(value))}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30000">30 seconds</SelectItem>
              <SelectItem value="60000">1 minute</SelectItem>
              <SelectItem value="300000">5 minutes</SelectItem>
              <SelectItem value="900000">15 minutes</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Manual Refresh Button */}
      <Button
        onClick={onManualRefresh}
        variant="outline"
        size="sm"
      >
        <RefreshCw className="w-4 h-4 mr-1" />
        Refresh
      </Button>
    </div>
  );
};

export default AutoRefreshControl;
